import {
  ObservableMap,
  action,
  computed,
  isObservableMap,
  observable,
  runInAction,
  toJS
} from 'mobx';
import { computedFn } from 'mobx-utils';

import { bind } from 'bind-decorator';
import moment from 'moment';
import validate from 'validate.js';

import JsonServiceClient from 'bz-stores/lib/JsonServiceClient';

import PromisesUtil from 'bz-utilities/modules/promises';
import type { IAllSettledRejected, TAllSettled } from 'bz-utilities/modules/promises';

import { cache } from 'tm3-core/cache';

import { ValidationEvent } from './ValidationEvent';

export interface IWithId {
  id?: any;
}

export type ValidationReason = 'Change' | 'Save';

export type TValidationRules<T> = Partial<Record<keyof T, any>>;

export type TValidationResults<T> = Partial<Record<keyof T, string[]>>;

export type ControlEvent<T> = {
  target: { value?: T; checked?: boolean };
  currentTarget: { value?: T; checked?: boolean };
};

function isControlEvent<T>(obj: any): obj is ControlEvent<T> {
  if (obj && obj.target) {
    if ('value' in obj.target) {
      return true;
    }
  }
  return false;
}

abstract class FormStore<T extends IWithId | any> {
  @observable isSubmitted: boolean = false;
  @observable hasChanges: boolean = false;
  @observable isLoading: boolean = true;
  @observable isSaving: boolean = false;
  @observable form: ObservableMap<keyof T, any> = new ObservableMap<keyof T, any>();
  @observable validationResults: TValidationResults<T> = {};
  @observable disabled: boolean = false;

  public readonly isEqual = computedFn(
    <K extends keyof T>(key: K, compare: T[K] | null): boolean => {
      const value = this.form.get(key);
      return value == compare;
    }
  );

  @observable
  public validationRules: TValidationRules<T> = {};

  protected validationOptions: any = { fullMessages: false };

  type: any;

  @computed
  public get isExisting(): boolean {
    return !!(this.form as ObservableMap<keyof IWithId, any>).get('id');
  }

  @computed
  get canSave() {
    // leave out isValid https://ux.stackexchange.com/questions/73728/should-form-continue-button-be-disabled-if-validation-is-incomplete/73733#73733
    return !this.isLoading && !this.isSaving && (!this.isExisting || this.hasChanges);
  }

  // Needed for conditional rendering
  @computed
  get isWorking() {
    return this.isLoading || this.isSaving;
  }

  @computed
  public get isValid(): boolean {
    const values = Object.values(this.validationResults) as (string[] | undefined)[];
    return !values.some((result?: string[]) => {
      return result && result.some && result.some((x) => !!x);
    });
  }

  get client() {
    return new JsonServiceClient('/api');
  }

  @bind
  public get<K extends keyof T>(key: K): T[K] {
    return this.form.get(key);
  }

  @bind
  set<K extends keyof T>(key: K, value: T[K]) {
    this.hasChanges = this.hasChanges || this.form.get(key) !== value;
    this.form.set(key, value);
    this.validate();
  }

  constructor(args: any) {
    this.loadStore(args);
  }

  @bind
  loadStore(args: any, delay: boolean = true) {
    if (args && args.validationOptions) {
      this.validationOptions = args.validationOptions;
    }

    if (!!this.beforeInitialise) {
      this.beforeInitialise();
    }

    validate.validators.optionalEmail = (value, options) => {
      if (value === '') {
        return null;
      }
      return validate.single(value, { email: options });
    };

    //Reference - https://validatejs.org/#validators-datetime
    validate.extend(validate.validators.datetime, {
      // The value is guaranteed not to be null or undefined but otherwise it
      // could be anything.
      parse: function (value, options) {
        return moment.utc(value);
      },

      format: function (value, options) {
        var format = options.dateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DDThh:mm:ss';
        return moment.utc(value).format(format);
      }
    });

    const loader = async () => {
      const data: any = await this.initialise(args);
      this.type = data?.constructor.name; // should be the generic Type constructor

      runInAction(async () => {
        (this.form as any as ObservableMap<T>).replace(Object.assign({}, data));
        this.validate('Change');
        if (!!this.onInitialise) {
          await this.onInitialise();
          this.hasChanges = false;
          await this.onAfterInitialise();
        }
      });
    };

    if (delay) {
      setTimeout(loader, 0);
    } else {
      loader();
    }
  }

  abstract initialise(args: any): Promise<T>;
  abstract onSave(data: T): Promise<any>;

  beforeInitialise(): void {
    return;
  }

  onInitialise(): void {
    return;
  }

  onAfterInitialise(): void {
    const meta = this.getMeta();
    if (!isObservableMap(meta)) {
      const { hasChanges } = this;
      this.setMeta(new ObservableMap(meta));
      this.hasChanges = hasChanges;
    }

    this.isLoading = false;
    return;
  }

  async onValidate(form: T | {}): Promise<boolean> {
    return true;
  }

  @action.bound
  public async save(throwErrors: boolean = false): Promise<any | false> {
    try {
      this.isSubmitted = true;
      const isValid = await this.validate('Save');
      if (!isValid) {
        return false;
      }
      runInAction(() => {
        this.isSaving = true;
      });

      const result = await this.onSave(toJS(this.form) as any as T);

      runInAction(() => {
        this.isSaving = false;
        this.hasChanges = false;
      });
      return result;
    } catch (ex) {
      this.isSaving = false;
      if (throwErrors) {
        throw ex;
      }
      return false;
    }
  }

  @action.bound
  onChange<TValue>(
    key: keyof T,
    callback: ((e: any) => void) | null = null,
    e: TValue | ControlEvent<TValue>
  ) {
    let value = e;
    if (isControlEvent<TValue>(e)) {
      value = e.target.value;
    }

    this.hasChanges = this.hasChanges || this.form.get(key) != value;
    this.form.set(key, value);
    if (callback && typeof callback === 'function') {
      callback(e);
    }
    if (!this.validationRules) {
      return;
    }
    const rule = this.validationRules[key];
    if (rule) {
      validate
        .async({ [key]: value }, { [key]: rule }, this.validationOptions)
        .then(() => {
          this.validationResults[key] = [];
        })
        .catch((errors) => {
          this.validationResults[key] = errors[key];
        });
    }
  }

  @bind
  bind<K extends keyof T>(key: K, callback?: (e: any) => void | null): FieldBinding<T[K]> {
    let errorText;
    try {
      errorText = (this.validationResults[key] || [])!.join(', ');
    } catch (e) {
      // hide exception - seemingly this.validationResults[key] can return "Error"
    }
    let value = this.form.get(key);
    if (value === null || value === undefined) {
      value = '';
    }
    return {
      value,
      onChange: this.onChange.bind(this, key, callback),
      submitted: this.isSubmitted,
      errorText: errorText,
      disabled: this.disabled
    };
  }

  @bind
  bindCheckbox(key: keyof T, callback?: (e: any) => void | null): CheckboxBinding {
    let errorText;
    try {
      errorText = (this.validationResults[key] || [])!.join(',');
    } catch (e) {
      // hide exception - seemingly this.validationResults[key] can return "Error"
    }
    return {
      checked: this.form.get(key) || '',
      onChange: this.onCheckboxChange.bind(this, key, callback),
      submitted: this.isSubmitted,
      errorText: errorText,
      disabled: this.disabled
    };
  }

  // #MERGE - introduction of a callback?
  //onCheckboxChange(key: keyof T, callback: (e: any) => void | null, e: any, checked: boolean) {
  //return this.onChange(key, callback, checked);
  @bind
  onCheckboxChange(key: keyof T, e: ControlEvent<boolean>, optionalEHack: ControlEvent<boolean>) {
    if (e) {
      return this.onChange(key, null, e.target.checked);
    } else {
      return this.onChange(key, null, optionalEHack.target.checked);
    }
  }

  @action.bound
  async validate(reason?: ValidationReason): Promise<boolean> {
    let form = this.serialise() as T;
    const { mediator } = cache;
    const validators = PromisesUtil.allSettled<true, object>([
      validate.async(form, this.validationRules, this.validationOptions),
      this.onValidate(form),
      ...(await mediator.map<Promise<boolean>, ValidationEvent<T>>(
        new ValidationEvent<T>({
          data: form,
          isSubmitted: this.isSubmitted,
          reason,
          type: this.type
        })
      ))
    ]);

    const result: TAllSettled<true, object>[] = await validators;
    const errored = result.filter((r) => {
      return r.status === 'rejected' || (r.status === 'fulfilled' && r.value === false);
    }) as IAllSettledRejected<object>[];

    runInAction(() => {
      this.validationResults = errored.reduce(
        (validationResults: object, r: IAllSettledRejected<object>) => {
          return { ...validationResults, ...r.reason };
        },
        {}
      );
    });
    return errored.length === 0;
  }

  @bind
  serialise(): T {
    if (!this.form) {
      return {} as T;
    }
    return Object.assign({}, toJS(this.form as any as Map<keyof T, any>)) as T;
  }

  @bind
  getMeta(): ObservableMap<string, Object> {
    return (
      (this.get as (k: 'meta') => ObservableMap<string, object>)('meta') ??
      new ObservableMap<string, Object>()
    );
  }

  @bind
  setMeta(meta: ObservableMap<string, Object>) {
    (this.set as (key: 'meta', value: ObservableMap<string, Object>) => void)('meta', meta);
  }
}

export interface FieldBinding<T = any> {
  errorText?: string;
  disabled?: boolean;
  submitted: boolean;
  value: T;

  onChange: any;
}

interface CheckboxBinding {
  checked: boolean;
  disabled?: boolean;
  errorText?: string;
  submitted: boolean;

  onChange: any;
}

export default FormStore;
export { FormStore };
